// client/src/components/Dashboard.jsx
import React, { useState, useEffect } from 'react';
import { Row, Col, Card, ProgressBar, Button, Table } from 'react-bootstrap';
import { Line, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend } from 'chart.js';
import axios from 'axios';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

const API_URL = process.env.REACT_APP_API_URL || '/api';

function Dashboard({ addAlert }) {
  const [stats, setStats] = useState({
    totalDesigns: 0,
    totalLabels: 0,
    labelsUsed: 0,
    labelsRemaining: 0,
    lowStockItems: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [inventoryData, setInventoryData] = useState([]);
  const [chartData, setChartData] = useState({ labels: [], datasets: [] });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch designs
      const designsRes = await axios.get(`${API_URL}/designs/read.php`);
      const designs = designsRes.data.records || [];
      
      // Fetch inventory
      const inventoryRes = await axios.get(`${API_URL}/inventory/get.php`);
      const inventory = inventoryRes.data.records || [];
      
      // Calculate stats
      const totalLabels = inventory.reduce((sum, item) => sum + item.total_labels, 0);
      const labelsUsed = inventory.reduce((sum, item) => sum + item.labels_used, 0);
      const labelsRemaining = inventory.reduce((sum, item) => sum + item.labels_remaining, 0);
      const lowStockItems = inventory.filter(item => item.status === 'Low').length;
      
      setStats({
        totalDesigns: designs.length,
        totalLabels,
        labelsUsed,
        labelsRemaining,
        lowStockItems
      });
      
      setInventoryData(inventory.slice(0, 5));
      
      // Prepare chart data
      const monthlyData = {
        labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
        datasets: [
          {
            label: 'Labels Printed',
            data: [1200, 1900, 1500, 2100, 1800, 2500],
            borderColor: '#8a2be2',
            backgroundColor: 'rgba(138, 43, 226, 0.1)',
            fill: true,
            tension: 0.4
          }
        ]
      };
      
      setChartData(monthlyData);
      
      // Simulate recent activity
      setRecentActivity([
        { id: 1, action: '250 Casual Wear labels printed', time: 'Today, 09:45 AM', type: 'print' },
        { id: 2, action: 'New design "Summer Collection" saved', time: 'Yesterday, 04:20 PM', type: 'design' },
        { id: 3, action: 'Roll #ROLL-002 low stock alert', time: 'May 14, 2023', type: 'inventory' },
        { id: 4, action: '500 Formal Wear labels printed', time: 'May 12, 2023', type: 'print' },
        { id: 5, action: 'New roll #ROLL-004 added', time: 'May 10, 2023', type: 'inventory' }
      ]);
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      addAlert('Failed to load dashboard data', 'danger');
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'Active': return <span className="status-badge status-active">Active</span>;
      case 'Low': return <span className="status-badge status-low">Low Stock</span>;
      case 'Empty': return <span className="status-badge status-empty">Empty</span>;
      default: return <span className="status-badge">{status}</span>;
    }
  };

  return (
    <div>
      <h2 className="mb-4">Dashboard</h2>
      
      {/* Stats Cards */}
      <Row className="mb-4">
        <Col md={3} sm={6}>
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted">Total Designs</h6>
                  <h3>{stats.totalDesigns}</h3>
                </div>
                <div className="text-primary fs-2">
                  <i className="fas fa-palette"></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={3} sm={6}>
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted">Total Labels</h6>
                  <h3>{stats.totalLabels.toLocaleString()}</h3>
                </div>
                <div className="text-success fs-2">
                  <i className="fas fa-tags"></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={3} sm={6}>
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted">Labels Used</h6>
                  <h3>{stats.labelsUsed.toLocaleString()}</h3>
                </div>
                <div className="text-warning fs-2">
                  <i className="fas fa-chart-line"></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={3} sm={6}>
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted">Low Stock Items</h6>
                  <h3 className={stats.lowStockItems > 0 ? 'text-danger' : ''}>
                    {stats.lowStockItems}
                  </h3>
                </div>
                <div className="text-danger fs-2">
                  <i className="fas fa-exclamation-triangle"></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {/* Chart and Inventory */}
      <Row className="mb-4">
        <Col lg={8}>
          <Card>
            <Card.Body>
              <Card.Title>Monthly Production Trends</Card.Title>
              <div className="chart-container">
                <Line data={chartData} options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'top',
                    }
                  }
                }} />
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={4}>
          <Card>
            <Card.Body>
              <Card.Title>Inventory Overview</Card.Title>
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <small>Labels Remaining</small>
                  <small>
                    {stats.totalLabels > 0
                      ? Math.round((stats.labelsRemaining / stats.totalLabels) * 100)
                      : 0}%
                  </small>
                </div>
                <ProgressBar 
                  now={stats.totalLabels > 0
                    ? (stats.labelsRemaining / stats.totalLabels) * 100
                    : 0}
                  variant="success"
                  className="progress-bar-custom"
                />
              </div>
              
              <Table hover size="sm">
                <thead>
                  <tr>
                    <th>Roll #</th>
                    <th>Status</th>
                    <th>Remaining</th>
                  </tr>
                </thead>
                <tbody>
                  {inventoryData.map((item) => (
                    <tr key={item.id}>
                      <td>{item.roll_number}</td>
                      <td>{getStatusBadge(item.status)}</td>
                      <td>{item.labels_remaining}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {/* Recent Activity */}
      <Row>
        <Col>
          <Card>
            <Card.Body>
              <Card.Title className="d-flex justify-content-between align-items-center">
                Recent Activity
                <Button variant="outline-primary" size="sm">
                  View All
                </Button>
              </Card.Title>
              <div className="activity-list">
                {recentActivity.map((activity) => (
                  <div key={activity.id} className="d-flex align-items-center mb-3 p-2 border rounded">
                    <div className={`me-3 p-2 rounded-circle ${activity.type === 'print' ? 'bg-primary' : activity.type === 'design' ? 'bg-success' : 'bg-warning'}`}>
                      <i className={`fas ${activity.type === 'print' ? 'fa-print' : activity.type === 'design' ? 'fa-palette' : 'fa-box'} text-white`}></i>
                    </div>
                    <div className="flex-grow-1">
                      <div>{activity.action}</div>
                      <small className="text-muted">{activity.time}</small>
                    </div>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default Dashboard;